import { useParams, useNavigate } from "react-router"
import { useAppSelector, useAppDispatch } from "../../hooks/reduxToolkit"
import { useEffect, useState, useCallback, useMemo } from "react"
import { useAsyncAction } from "../../hooks/useAsyncAction"
import { BlobRow, AttachmentPurpose } from "../../../types/supabase"
import { formatCurrency } from "../../../lib/formatCurrency"
import { formatDateInTimeZone } from "../../../lib/formatDateInTimeZone"
import { ExpenseModel, updateExpense } from "../../reduxToolkit/expensesSlice"
import { extractExpenseFromTextract } from "../../../lib/util/extractExpenseFromTextract"
import { createAttachment } from "../../reduxToolkit/attachmentsSlice"
import { useUser } from "../../hooks/useUser"
import { v4 as uuidv4 } from 'uuid'

import './attachToExpense.scss'

export function AttachToExpense() {
  const { blobKey } = useParams<{ blobKey: string }>()
  const user = useUser()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const expenses = useAppSelector((s) => s.expenses.expenses)
  const incidents = useAppSelector((s) => s.incidents.incidents)
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const allBlobs = useAppSelector((s) => s.blobs.blobs)

  const [selectedPurpose, setSelectedPurpose] = useState<AttachmentPurpose>('receipt')

  // Get blob from Redux instead of querying Supabase
  const blob = allBlobs.find((b) => b.key === blobKey)

  // Check if there's a classification mismatch warning to show
  const getClassificationWarning = useCallback(() => {
    if (!blob || !blob.textract_job_results) return null

    // Check if Claude has classified this blob
    const results = blob.textract_job_results as any
    if (results.document_classification?.type && results.document_classification?.reason) {
      const claudeClassification = results.document_classification.type
      const claudeReason = results.document_classification.reason

      // Show warning if Claude says it's a receipt but user wants to attach as itemized bill
      if (claudeClassification != 'itemized-bill' && selectedPurpose === 'itemized-bill') {
        return {
          type: 'warning',
          message: `This doesn't look like an itemized bill! ${claudeReason}`
        }
      }
    }

    return null
  }, [blob, selectedPurpose])

  useEffect(() => {
    if (blob?.blob_original_purpose) {
      setSelectedPurpose(blob.blob_original_purpose)
    }
  }, [blob?.blob_original_purpose])
  
  // Build a set of closed incident IDs
  const closedIncidentIds = useMemo(() =>
    new Set(incidents.filter(i => i.closed_at).map(i => i.id)),
    [incidents]
  )

  // Filter expenses to only show open ones (non-deleted, non-submitted, not from closed incidents)
  const openExpenses = expenses.filter(expense =>
    !expense.deleted_at && !expense.submitted_at &&
    (!expense.incident_id || !closedIncidentIds.has(expense.incident_id))
  )
  
  // Attach receipt to selected expense
  const [attachReceiptState, attachReceipt] = useAsyncAction(async (expense: ExpenseModel) => {
    if (!blob) throw new Error('Blob not loaded')
    
    // 1. Create attachment linking receipt to expense
    let now = new Date().toISOString();
    dispatch(createAttachment({
      id: uuidv4(),
      created_by_user_id: user?.id,
      blob_key: blob.key,
      table_name: 'expenses',
      record_id: expense.id,
      created_at: now,
      updated_at: now,
      membership_id: membershipId || expense.membership_id,
      purpose: selectedPurpose,
    }));
    
    // 2. Update expense fields with Textract data if not already filled
    const extractedData = extractExpenseFromTextract(blob)
    if (extractedData) {
      const updates: Partial<ExpenseModel> = {}
      
      // Only update fields that are empty
      if (extractedData.date && !expense.date) {
        updates.date = extractedData.date
      }
      if (extractedData.provider && !expense.provider) {
        updates.provider = extractedData.provider
      }
      if (extractedData.listedAmount && !expense.listedAmount) {
        updates.listedAmount = extractedData.listedAmount
      }
      if (extractedData.paidAmount && !expense.paidAmount) {
        updates.paidAmount = extractedData.paidAmount
      }
      
      // Update expense if we have any fields to update
      if (Object.keys(updates).length > 0) {
        const updatedExpense = {
          ...expense,
          ...updates,
          updated_at: now
        }
                
        // Update Redux state
        dispatch(updateExpense(updatedExpense))
      }
    }
    
    // 3. Navigate based on the expense's state.
    // Draft expenses (e.g. Plaid-imported charges) aren't finished yet, so send
    // the user straight to the edit screen to complete them — the attachment
    // created above is already linked and will show up there.
    if (expense.is_draft === true) {
      navigate(`/expenses/${expense.id}/edit`)
    } else if (expense.incident_id) {
      navigate(`/incidents/${expense.incident_id}`, {
        state: { expense_id: expense.id }
      })
    } else {
      navigate('/expenses', {
        state: { expense_id: expense.id }
      })
    }
  }, [blob, navigate, selectedPurpose])
  
  const handleExpenseSelect = useCallback((expense: ExpenseModel) => {
    attachReceipt(expense)
  }, [attachReceipt])
  
  const handleCreateNewExpense = useCallback(() => {
    // Extract expense data from Textract results if available
    const extractedData = extractExpenseFromTextract(blob!)
    
    // Navigate to new expense form with pre-populated data and blob key for attachment
    navigate('/expenses/new', {
      state: {
        prefilledData: extractedData || {},
        blobKey: blob!.key
      }
    })
  }, [blob, navigate])

  if (!blob) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              Receipt not found
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="container attach-to-expense">
      <div className="row">
        <div className="col-12">
          <div className="mb-4">
            <h1>Attach Receipt to Expense</h1>
            <p className="text-muted mb-2">
              Receipt: <strong>{blob.file_name}</strong>
            </p>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label"><strong>Attach as</strong></label>
                <select
                  className="form-select"
                  value={selectedPurpose}
                  onChange={(e) => setSelectedPurpose(e.target.value as AttachmentPurpose)}
                >
                  <option value="receipt">Receipt</option>
                  <option value="itemized-bill">Itemized Bill</option>
                </select>
              </div>
            </div>

            {/* Show classification warning if there's a mismatch */}
            {getClassificationWarning() && (
              <div className="d-flex align-items-start gap-2 mt-3 text-warning">
                <i className="material-icons" style={{ fontSize: '20px' }}>warning</i>
                <div>{getClassificationWarning()!.message}</div>
              </div>
            )}
          </div>

          {attachReceiptState.loading && (
            <div className="alert alert-info">
              <div className="d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Processing...</span>
                </div>
                Attaching receipt to expense...
              </div>
            </div>
          )}
          
          {attachReceiptState.error && (
            <div className="alert alert-danger">
              Failed to attach receipt: {attachReceiptState.error.message}
            </div>
          )}
          
          {openExpenses.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="material-icons mb-3" style={{ fontSize: '48px' }}>receipt_long</i>
              <h3>No Open Expenses</h3>
              <p className="text-muted">
                You don't have any open expenses to attach this receipt to.
              </p>
              <button 
                className="btn btn-primary"
                onClick={handleCreateNewExpense}
              >
                Create New Expense
              </button>
            </div>
          ) : (
            <>
              <p className="mb-3">
                Select an expense below to attach the receipt. Fields will be automatically filled from the receipt if they're empty.
              </p>
              
              <div className="expense-selection-list">
                {openExpenses.map(expense => (
                  <div 
                    key={expense.id}
                    className="expense-card card mb-3"
                    onClick={() => handleExpenseSelect(expense)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>
                              {expense.date ? formatDateInTimeZone(expense.date, { 
                                format: 'MMM d, yyyy', 
                                timeZone: 'America/New_York' 
                              }) : 'No date'}
                            </strong>
                            <div className="text-end">
                              {expense.listedAmount && (
                                <div className="text-success fw-bold">
                                  {formatCurrency(expense.listedAmount)}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-md-6">
                              {expense.patient_name && (
                                <div className="text-muted small">
                                  <strong>Patient:</strong> {expense.patient_name}
                                </div>
                              )}
                              {expense.provider && (
                                <div className="text-muted small">
                                  <strong>Provider:</strong> {expense.provider}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6">
                              {expense.incident_id && (
                                <div className="text-muted small">
                                  <strong>Incident:</strong> #{expense.incident_id.slice(-8)}
                                </div>
                              )}
                              {expense.paidAmount && (
                                <div className="text-muted small">
                                  <strong>Paid:</strong> {formatCurrency(expense.paidAmount)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="ms-3">
                          <i className="material-icons text-muted">arrow_forward</i>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}